/**
 * Sortable category row for drag-and-drop reordering.
 * Use within DndContext and SortableContext (verticalListSortingStrategy).
 */

import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { DragOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import type { KBCategory } from '@municipality/types';

function getDisplayName(
  translations: Record<string, { name?: string }>,
  primaryLanguage: string
): string {
  const primary = translations[primaryLanguage]?.name;
  if (primary) return primary;
  const en = translations.en?.name;
  if (en) return en;
  const first = Object.values(translations)[0]?.name;
  return first ?? '';
}

export interface SortableCategoryRowProps {
  category: KBCategory;
  articleCount?: number;
  onEdit?: (category: KBCategory) => void;
  onDelete?: (category: KBCategory) => void;
}

export const SortableCategoryRow: React.FC<SortableCategoryRowProps> = ({
  category,
  articleCount = 0,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayName = getDisplayName(
    category.translations,
    category.languageSettings.primaryLanguage
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: isDragging ? '#fafafa' : 'transparent',
        borderRadius: '6px',
        margin: '4px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: isDragging ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
      }}
    >
      <div
        style={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          borderRadius: '4px',
          backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
        }}
        {...attributes}
        {...listeners}
      >
        <DragOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong style={{ fontSize: '16px' }}>
          {displayName || t('categories.name')}
        </Typography.Text>
        <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!category.isActive && (
            <Tag color="default">{t('common:common.inactive')}</Tag>
          )}
          {category.showOnLanding && (
            <Tag color="blue">{t('knowledgeBase:categories.showOnLanding')}</Tag>
          )}
          {articleCount >= 0 && (
            <Typography.Text type="secondary">
              {articleCount} {t('knowledgeBase:categories.articleCount')}
            </Typography.Text>
          )}
        </div>
      </div>

      <Space size="small">
        {onEdit && (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(category)}
            aria-label={t('common:common.edit')}
          />
        )}
        {onDelete && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(category)}
            aria-label={t('common:common.delete')}
          />
        )}
      </Space>
    </div>
  );
};
